import React, { useState, useEffect, useCallback } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { 
  LayoutDashboard, 
  Calendar, 
  Users, 
  Clock, 
  Settings, 
  LogOut,
  Menu,
  X,
  FileText,
  MessageSquare,
  UserCog,
  Stethoscope,
  Package,
  Building2,
  ShoppingCart,
  DollarSign,
  ChevronDown,
  ChevronRight,
  TrendingUp,
  AlertCircle,
  Key,
  UserX,
  Upload
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { Badge } from '../components/ui/badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Map nav items to permission keys
const NAV_PERMISSION_MAP = {
  '/admin/dashboard': 'dashboard',
  '/admin/calendar': 'calendario',
  '/admin/agenda': 'agenda',
  '/admin/no-asistieron': 'no_asistieron',
  '/admin/patients': 'pacientes',
  '/admin/crm-cirugias': 'crm_cirugias',
  '/admin/ventas': 'ventas',
  '/admin/finanzas': 'finanzas',
  '/admin/inventario': 'inventario',
  '/admin/proveedores': 'proveedores',
  '/admin/compras': 'compras',
  '/admin/whatsapp': 'whatsapp',
  '/admin/settings': 'configuracion',
  '/admin/importacion': 'importacion'
};

const AdminLayout = () => {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(location.pathname.includes('/admin/settings'));
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [alerts, setAlerts] = useState({
    cxpVencidas: 0,
    cxpTotal: 0,
    cirugiasContactar: 0,
    cirugiasVencidas: 0,
    noAsistieron: 0
  });

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('Las contraseñas no coinciden');
      return;
    }
    
    if (passwordForm.newPassword.length < 8) {
      toast.error('La contraseña debe tener al menos 8 caracteres');
      return;
    }
    
    try {
      await axios.put(`${API}/auth/change-password`, {
        current_password: passwordForm.currentPassword,
        new_password: passwordForm.newPassword
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Contraseña actualizada exitosamente');
      setShowChangePassword(false);
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cambiar contraseña');
    }
  };

  // Fetch all sidebar alerts
  const fetchAlerts = useCallback(async () => {
    if (!token || !['admin', 'superadmin'].includes(user?.role)) return;
    
    try {
      const [purchasesRes, surgeryRes, noShowRes] = await Promise.all([
        axios.get(`${API}/purchases/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: {} })),
        axios.get(`${API}/surgery-crm/dashboard/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: {} })),
        axios.get(`${API}/appointments/no-show/count`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { days: 30 }
        }).catch(() => ({ data: { count: 0 } }))
      ]);
      
      setAlerts({
        cxpVencidas: purchasesRes.data.facturas_vencidas || 0,
        cxpTotal: purchasesRes.data.cxp_pendientes || 0,
        cirugiasContactar: surgeryRes.data.contactar_hoy || 0,
        cirugiasVencidas: surgeryRes.data.vencidos || 0,
        noAsistieron: noShowRes.data.count || 0
      });
    } catch (error) {
      console.error('Error fetching alerts:', error);
    }
  }, [token, user?.role]);

  useEffect(() => {
    fetchAlerts();
    // Refresh every 5 minutes
    const interval = setInterval(fetchAlerts, 300000);
    return () => clearInterval(interval);
  }, [fetchAlerts]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Check if user has permission for a route
  const hasPermission = useCallback((route) => {
    // Superadmin and admin have full access
    if (user?.role === 'superadmin' || user?.role === 'admin') {
      return true;
    }
    
    // If no permissions defined, default to showing all (backwards compatibility)
    if (!user?.permisos || user.permisos.length === 0) {
      return true;
    }
    
    // Check specific permission
    const permissionKey = NAV_PERMISSION_MAP[route];
    if (!permissionKey) return true; // If no mapping, show by default
    
    return user.permisos.includes(permissionKey);
  }, [user?.role, user?.permisos]);

  const allNavItems = [
    { to: '/admin/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { to: '/admin/calendar', icon: Calendar, label: 'Calendario' },
    { 
      to: '/admin/no-asistieron', 
      icon: UserX, 
      label: 'No Asistieron',
      alertCount: alerts.noAsistieron,
      alertColor: 'bg-red-500',
      alertTooltip: `${alerts.noAsistieron} pendientes de contactar`
    },
    { to: '/admin/patients', icon: Users, label: 'Pacientes' },
    { 
      to: '/admin/crm-cirugias', 
      icon: Stethoscope, 
      label: 'CRM Cirugías',
      alertCount: alerts.cirugiasContactar + alerts.cirugiasVencidas,
      alertColor: alerts.cirugiasVencidas > 0 ? 'bg-red-500' : 'bg-amber-500',
      alertTooltip: alerts.cirugiasVencidas > 0 
        ? `${alerts.cirugiasVencidas} vencidos, ${alerts.cirugiasContactar} contactar hoy`
        : `${alerts.cirugiasContactar} contactar hoy`
    },
    { to: '/admin/ventas', icon: DollarSign, label: 'Ventas' },
    { to: '/admin/finanzas', icon: TrendingUp, label: 'Finanzas' },
    { to: '/admin/inventario', icon: Package, label: 'Inventario' },
    { to: '/admin/proveedores', icon: Building2, label: 'Proveedores' },
    { 
      to: '/admin/compras', 
      icon: ShoppingCart, 
      label: 'Compras', 
      alertCount: alerts.cxpVencidas,
      alertColor: 'bg-red-500',
      alertTooltip: `${alerts.cxpVencidas} facturas vencidas`
    },
    { to: '/admin/whatsapp', icon: MessageSquare, label: 'WhatsApp' },
    { to: '/admin/importacion', icon: Upload, label: 'Importación' },
    { 
      to: '/admin/settings/availability', 
      icon: Settings, 
      label: 'Configuración',
      subItems: [
        { to: '/admin/settings/availability', label: 'Disponibilidad' },
        { to: '/admin/settings/users', label: 'Usuarios' },
        { to: '/admin/settings/email', label: 'Notificaciones Email' },
      ]
    },
  ];

  // Filter nav items based on user permissions
  const navItems = allNavItems.filter(item => {
    // For settings, check if user has 'configuracion' permission
    if (item.to.startsWith('/admin/settings')) {
      return hasPermission('/admin/settings');
    }
    return hasPermission(item.to);
  });

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Mobile Menu Button */}
      <button
        className="lg:hidden fixed top-4 left-4 z-50 p-2 rounded-lg bg-white shadow-md"
        onClick={() => setSidebarOpen(!sidebarOpen)}
        data-testid="mobile-menu-button"
      >
        {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
      </button>

      {/* Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside 
        className={`sidebar fixed lg:static inset-y-0 left-0 z-40 w-64 transform transition-transform duration-200 ease-in-out ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-white/10">
            <img 
              src="https://customer-assets.emergentagent.com/job_voicemd-platform/artifacts/xuohzkv0_Logo%20nuevo%20.png" 
              alt="Logo"
              className="h-36 mx-auto brightness-0 invert opacity-90"
            />
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {navItems.map((item) => (
              item.subItems ? (
                <div key={item.label}>
                  <button
                    onClick={() => setSettingsOpen(!settingsOpen)}
                    className={`w-full sidebar-item flex items-center justify-between gap-3 px-4 py-3 rounded-lg text-white/80 hover:text-white ${
                      location.pathname.includes('/admin/settings') ? 'active bg-white/10' : ''
                    }`}
                    data-testid={`nav-${item.label.toLowerCase()}`}
                  >
                    <div className="flex items-center gap-3">
                      <item.icon className="h-5 w-5" />
                      <span className="font-medium">{item.label}</span>
                    </div>
                    {settingsOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  </button>
                  {settingsOpen && (
                    <div className="ml-4 mt-1 space-y-1">
                      {item.subItems.map((subItem) => (
                        <NavLink
                          key={subItem.to}
                          to={subItem.to}
                          onClick={() => setSidebarOpen(false)}
                          className={({ isActive }) => 
                            `sidebar-item flex items-center gap-3 px-4 py-2 rounded-lg text-white/70 hover:text-white text-sm ${
                              isActive ? 'active bg-white/10 text-white' : ''
                            }`
                          }
                        >
                          <span>{subItem.label}</span>
                        </NavLink>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={() => setSidebarOpen(false)}
                  className={({ isActive }) => 
                    `sidebar-item flex items-center gap-3 px-4 py-3 rounded-lg text-white/80 hover:text-white ${
                      isActive ? 'active bg-white/10' : ''
                    }`
                  }
                  data-testid={`nav-${item.label.toLowerCase()}`}
                  title={item.alertTooltip}
                >
                  <item.icon className="h-5 w-5" />
                  <span className="font-medium">{item.label}</span>
                  {item.alertCount > 0 && (
                    <span 
                      className={`ml-auto flex h-5 min-w-[20px] items-center justify-center rounded-full ${item.alertColor} text-xs text-white font-bold px-1`}
                    >
                      {item.alertCount > 99 ? '99+' : item.alertCount}
                    </span>
                  )}
                </NavLink>
              )
            ))}
          </nav>

          {/* User Section */}
          <div className="p-4 border-t border-white/10">
            <div className="flex items-center gap-3 px-4 py-3 text-white/80">
              <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                <span className="text-sm font-medium">{user?.name?.charAt(0) || 'A'}</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{user?.name || 'Admin'}</p>
                <p className="text-xs text-white/50 truncate">@{user?.username}</p>
              </div>
            </div>
            <Button
              variant="ghost"
              className="w-full mt-1 text-white/70 hover:text-white hover:bg-white/10 justify-start"
              onClick={() => setShowChangePassword(true)}
              data-testid="change-own-password-button"
            >
              <Key className="h-4 w-4 mr-2" />
              Cambiar Contraseña
            </Button>
            <Button
              variant="ghost"
              className="w-full mt-1 text-white/70 hover:text-white hover:bg-white/10 justify-start"
              onClick={handleLogout}
              data-testid="logout-button"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Cerrar Sesión
            </Button>
          </div>
        </div>
      </aside>

      {/* Change Password Dialog */}
      <Dialog open={showChangePassword} onOpenChange={setShowChangePassword}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#0B3B71] flex items-center gap-2">
              <Key className="h-5 w-5" />
              Cambiar Mi Contraseña
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div className="space-y-2">
              <Label>Contraseña Actual</Label>
              <Input
                type="password"
                value={passwordForm.currentPassword}
                onChange={(e) => setPasswordForm(prev => ({ ...prev, currentPassword: e.target.value }))}
                placeholder="Ingresa tu contraseña actual"
                required
                data-testid="current-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Nueva Contraseña</Label>
              <Input
                type="password"
                value={passwordForm.newPassword}
                onChange={(e) => setPasswordForm(prev => ({ ...prev, newPassword: e.target.value }))}
                placeholder="Mínimo 8 caracteres"
                required
                minLength={8}
                data-testid="new-password-input"
              />
              <p className="text-xs text-slate-400">
                Debe incluir mayúsculas, minúsculas y números
              </p>
            </div>
            <div className="space-y-2">
              <Label>Confirmar Nueva Contraseña</Label>
              <Input
                type="password"
                value={passwordForm.confirmPassword}
                onChange={(e) => setPasswordForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                placeholder="Repite la nueva contraseña"
                required
                minLength={8}
                data-testid="confirm-password-input"
              />
            </div>
            <div className="flex gap-2 pt-4">
              <Button 
                type="button" 
                variant="outline" 
                className="flex-1" 
                onClick={() => {
                  setShowChangePassword(false);
                  setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
                }}
              >
                Cancelar
              </Button>
              <Button 
                type="submit" 
                className="flex-1 bg-[#0B3B71] hover:bg-[#0a3460]"
                data-testid="save-password-button"
              >
                Guardar Contraseña
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Main Content */}
      <main className="flex-1 min-h-screen lg:ml-0">
        <div className="p-4 lg:p-8 pt-16 lg:pt-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
